import React from "react";

import { Grid } from "@material-ui/core";

import {
  Edit,
  FormDataConsumer,
  maxLength,
  minLength,
  RadioButtonGroupInput,
  regex,
  required,
  SimpleForm,
  TextInput,
  Toolbar,
  TopToolbar,
  useEditController,
} from "react-admin";

import { BackButton } from "../../../../components/common/BackButton";
import { ForceTitle } from "../../../../components/common/ForceTitle";
import { HelpButton } from "../../../../components/common/HelpButton";
import {
  REGEX_PHONE,
  TrueOrFalse,
} from "../../../../utils/constants";
import {
  hasIdDocFirstNameMismatch,
  hasIdDocLastNameMismatch,
  isCarrierUserType,
  parsePhoneDigits,
} from "../../../../utils/carrierUserUtils";
import { getBackPath } from "../../../../utils/utils";
import { CarrierUserEditHelp } from "./CarrierUserHelp";
import { useDriverIdentity } from "./useDriverIdentity";
import { DeepDeleteButton } from "./DeepDeleteButton";
import { MoveCarrierButton } from "./MoveCarrierButton";
import { ValidateSaveButton } from "./ValidateSaveButton";
import { CarrierUserAside } from "./CarrierUserAside";
import { SendInviteButton } from "../../../../components/common/SendInviteButton";

const validateRequired = required();
const validatePhone = [
  regex(REGEX_PHONE, "Must be valid Phone No."),
  minLength(10),
  maxLength(20),
];
// Required of a driver only. A driver is found at the gate by (phone, lastName)
// on the exact-match carrierUserByPhone index, so a cleared number takes them
// off it with nothing to say so. A dispatch user is reached by email and has no
// such handle — and the primary account is created from the carrier's own
// address by sendPrimaryDispatchInvite, which carries no phone at all because
// the carrier's own is optional. Requiring one of everybody left that account
// unsavable until somebody invented a number for it.
//
// Both arrays are module-level so the identity react-final-form registers the
// field with is stable across renders.
const validateDriverPhone = [required(), ...validatePhone];

const CarrierEditForm = ({ record, identity, ...props }) => {
  return (
    <FormDataConsumer>
      {({ formData, ...rest }) => (
        <Grid container spacing={5}>
          <Grid item xs={12} md={8}>
            <Grid container direction="column" spacing={1}>
              {!isCarrierUserType(window.appUser.type) && (
                  <Grid container direction="row" spacing={2}>
                    <Grid item xs={12} lg={6}>
                      <TextInput
                        source="reloadYardCarriers.items[0].businessName"
                        label="Business Name"
                        variant="outlined"
                        fullWidth
                        validate={validateRequired}
                        disabled={true}
                      />
                    </Grid>
                  </Grid>
                )}
              <Grid container direction="row" spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextInput
                    source="firstName"
                    label="First Name"
                    variant="outlined"
                    fullWidth
                    validate={validateRequired}
                    disabled={!isCarrierUserType(window.appUser.type)}
                    helperText={
                      hasIdDocFirstNameMismatch(formData, identity)
                        ? `Does not match document (${identity.idDocFirstInitial})`
                        : undefined
                    }
                    FormHelperTextProps={
                      hasIdDocFirstNameMismatch(formData, identity)
                        ? { error: true }
                        : undefined
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextInput
                    source="lastName"
                    label="Last Name"
                    variant="outlined"
                    fullWidth
                    validate={validateRequired}
                    disabled={!isCarrierUserType(window.appUser.type)}
                    helperText={
                      hasIdDocLastNameMismatch(formData, identity)
                        ? `Does not match document (${identity.idDocLastName})`
                        : undefined
                    }
                    FormHelperTextProps={
                      hasIdDocLastNameMismatch(formData, identity)
                        ? { error: true }
                        : undefined
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextInput
                    source="email"
                    type="email"
                    variant="outlined"
                    fullWidth
                    parse={(v) => (v !== "" ? v.toLowerCase() : undefined)}
                    disabled={true}
                  />
                </Grid>
                {formData.userType === "driver" && (
                  <Grid item xs={12} md={6}>
                    <TextInput
                      source="contactEmail"
                      label="Contact email (from kiosk)"
                      type="email"
                      variant="outlined"
                      fullWidth
                      disabled={true}
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <TextInput
                    source="phone"
                    type="tel"
                    variant="outlined"
                    fullWidth
                    parse={parsePhoneDigits}
                    validate={
                      formData.userType === "driver"
                        ? validateDriverPhone
                        : validatePhone
                    }
                    disabled={!isCarrierUserType(window.appUser.type)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextInput
                    source="reloadYardCarriers.items[0].notes"
                    label="Notes"
                    variant="outlined"
                    multiline
                    rows={3}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={4}>
            <Grid container direction="column" spacing={1}>
              {!isCarrierUserType(window.appUser.type) && (
                  <Grid item xs={12}>
                    <RadioButtonGroupInput
                      source="reloadYardCarriers.items[0].blockAccess"
                      label="Block Access"
                      choices={TrueOrFalse}
                      defaultValue={false}
                      row={true}
                      validate={validateRequired}
                    />
                  </Grid>
                )}
            </Grid>
          </Grid>
        </Grid>
      )}
    </FormDataConsumer>
  );
};

const CarrierEditActions = ({ data, ...props }) => (
  <TopToolbar>
    <HelpButton>
      <CarrierUserEditHelp {...props} />
    </HelpButton>
    <SendInviteButton
      label="INVITE"
      title="Resend invitation"
      data={data}
      {...props}
    />
    {/* Renders nothing but a driver with a live membership here — a dispatch
        user's carrier is corrected by deleting and re-inviting them. */}
    <MoveCarrierButton record={data} />
    {/* Global suspension is the carrier's power (carrierApp) — a yard manager
        blocks locally via the Block Access field instead. Deleting a driver's
        personal data is nobody's power here: a driver ID travels with the
        person between transloads, so withdrawing it is theirs to do at the
        kiosk ("Delete my driver ID", face-verified). Delete, below, ends this
        transload's access and takes the record with it only when no other
        transload and no check-in history has a claim on it. */}
    <BackButton {...props} />
  </TopToolbar>
);

const CarrierEditToolbar = ({ record, ...props }) => (
  <Toolbar style={{ justifyContent: "space-between" }} {...props}>
    <ValidateSaveButton {...props} record={record} />
    <DeepDeleteButton record={record} />
  </Toolbar>
);

export const CarrierUserEdit = (props) => {
  const { record } = useEditController(props);
  const backPath = getBackPath(props);
  const identity = useDriverIdentity(record);

  return (
    <Edit
      {...props}
      title={<ForceTitle title="Edit User" />}
      mutationMode="pessimistic"
      actions={<CarrierEditActions backPath={backPath} />}
      aside={
        <CarrierUserAside {...props} backPath={backPath} identity={identity} />
      }
    >
      <SimpleForm
        variant="outlined"
        submitOnEnter={false}
        toolbar={<CarrierEditToolbar record={record} />}
      >
        <CarrierEditForm record={record} identity={identity} />
      </SimpleForm>
    </Edit>
  );
};
